import {FC} from 'react'
import {useTranslation} from 'react-i18next'
import {NavLink, useHistory} from 'react-router-dom'
import {FormModelType, useForm} from 'reactjs-use-form'

import {Box, Link, TextField} from '@mui/material'
import {getProductLineColors, PGTypography} from '@progynyglobal/pg-ui-library'
import {useSnackbar} from 'notistack'
import * as Yup from 'yup'

import signupPngSrc from '@progyny/assets/images/side-banner-white-top.png'
import SubmitButton from '@progyny/components/Buttons/SubmitButton'
import {SidepanelContainer} from '@progyny/components/SidepanelContainer'
import {useSendConfirmRegistrationEmailMutation} from '@progyny/generated/graphql'
import {useErrorCodes} from '@progyny/hooks/useErrorCodes'
import {useStyles} from '@progyny/pages/Signup'
import {RouteEnum, useRoutes} from '@progyny/routes'

export type AlternativeRegisterField = {
  name: string
  labelKey: string
  placeholder: string
  validate?: (value: string) => boolean
  invalidMessageKey: string
}

export type AlternativeRegisterProps = {
  firstField: AlternativeRegisterField
  secondField: AlternativeRegisterField
  submitButtonLabelKey: string
  alternativeISN?: boolean
}

const AlternativeRegister: FC<AlternativeRegisterProps> = ({
  firstField,
  secondField,
  submitButtonLabelKey,
  alternativeISN = false,
}) => {
  const getErrorMessage = useErrorCodes()
  const history = useHistory()
  const {getPath} = useRoutes()
  const {enqueueSnackbar} = useSnackbar()
  const {classes} = useStyles()
  const {t} = useTranslation()
  const emailValidation = Yup.string().email().nullable()
  const theme = getProductLineColors('fertility')

  const [sendConfirmRegistrationEmail, {loading}] =
    useSendConfirmRegistrationEmailMutation()

  const noAuthLanguage = localStorage.getItem('ui_language') || 'en'

  const formModel: FormModelType = {
    [firstField.name]: {
      value: '',
      required: true,
      validator: firstField.validate
        ? (value) => {
            if (firstField.validate!(value)) return ''
            else return t(firstField.invalidMessageKey)
          }
        : undefined,
    },
    [secondField.name]: {
      value: '',
      required: true,
      validator: secondField.validate
        ? (value) => {
            if (secondField.validate!(value)) return ''
            else return t(secondField.invalidMessageKey)
          }
        : undefined,
    },
    email: {
      value: '',
      required: true,
      validator: (email) => {
        if (!emailValidation.isValidSync(email)) {
          return getErrorMessage('invalid-email')
        } else return ''
      },
    },
  }

  const handleSubmit = () => {
    sendConfirmRegistrationEmail({
      variables: {
        email: values.email,
        language: noAuthLanguage,
        alternativeAuth:
          values[firstField.name] +
          (alternativeISN ? '-' : ' ') +
          values[secondField.name],
      },
    })
      .then((response) => {
        const data = response.data!.sendConfirmRegistrationEmail

        if (data!.success) {
          // redirect to the email-requested page
          return history.push(getPath(RouteEnum.REGISTER_EMAIL_REQUESTED))
        } else {
          // show the first error message
          const error = data!.errors![0]
          const message =
            error!.field === '__all__'
              ? error!.messages[0]
              : `${error!.field}: ${error!.messages[0]}`
          enqueueSnackbar({message, variant: 'error'})
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const {values, errors, isDisabled, handleOnSubmit, handleOnChange} = useForm(
    formModel,
    handleSubmit
  )

  return (
    <SidepanelContainer
      styleProps={{
        backgroundPicSrc: signupPngSrc,
        backgroundColor: theme.primary,
        logoColor: 'white',
      }}
      mainComponent={
        <Box className={classes.formContainer}>
          <PGTypography className={classes.register} textAlign={'center'}>
            {t('register_complete_register_to_progyny')}
          </PGTypography>

          <Box whiteSpace={'nowrap'} fontSize={'inherit'}>
            <PGTypography textAlign={'center'} sx={{marginBottom: '32px'}}>
              {t('register_request_email_already_have_account')}{' '}
              <Link component={NavLink} to={getPath(RouteEnum.LOGIN)}>
                {t('register_request_email_please_login')}
              </Link>
            </PGTypography>
          </Box>

          <form onSubmit={handleOnSubmit}>
            <Box>
              <TextField
                size='small'
                variant='outlined'
                label={t(firstField.labelKey)}
                margin='dense'
                placeholder={firstField.placeholder}
                name={firstField.name}
                value={values[firstField.name]}
                onChange={handleOnChange}
                error={errors?.[firstField.name].hasError}
                helperText={t(errors?.[firstField.name].message)}
                fullWidth
              />
            </Box>

            <Box mt={3}>
              <TextField
                size='small'
                variant='outlined'
                label={t(secondField.labelKey)}
                margin='dense'
                placeholder={secondField.placeholder}
                name={secondField.name}
                value={values[secondField.name]}
                onChange={handleOnChange}
                error={errors?.[secondField.name].hasError}
                helperText={t(errors?.[secondField.name].message)}
                fullWidth
              />
            </Box>

            <Box mt={3}>
              <TextField
                size='small'
                variant='outlined'
                label={t('register_request_email_company_email_address')}
                margin='dense'
                placeholder={t('yourname@yourcompany.com')}
                name='email'
                value={values.email}
                onChange={handleOnChange}
                error={errors?.email.hasError}
                helperText={t(errors?.email.message)}
                fullWidth
                inputProps={{
                  autoComplete: 'email',
                  'aria-labelledby': 'email-label',
                }}
              />
            </Box>

            <SubmitButton
              type='submit'
              loading={loading}
              disabled={isDisabled}
              className={classes.button}
              fullWidth
            >
              {t(submitButtonLabelKey)}
            </SubmitButton>
          </form>
        </Box>
      }
    />
  )
}

export default AlternativeRegister
